/* eslint-disable react/no-unused-prop-types */
import {
  Checkbox,
  FormGroup,
  H6,
  Icon,
  InputGroup,
  PopoverPosition,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import debounce from 'lodash/debounce';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import {
  GenericProperty,
  GenericPropertySuggestion,
  GenericPropertySuggestionType,
  ItemSelector,
  ManagedGenericPropertiesEditor,
  Tooltip,
} from 'vet-bones/components';
import { isEqual } from 'vet-bones/utils';
import { SelectItem, stringToSelectItem } from 'vet-bones/utils/select';

import { AdvancedOptions } from 'src/components/AdvancedOptions';
import { MAX_PROPERTY_SUGGESTION_ITEMS } from 'src/constants/limits';
import { VIRTUAL_GRAPH_OPTIONS_DOCUMENTATION_LINK } from 'src/constants/links';
import {
  TEST_IDS,
  getProjectVirtualGraphPanelAdvancedPropertyTestId,
  getProjectVirtualGraphPanelPropertyTestId,
  getProjectVirtualGraphPanelSqlQuotingItemTestId,
} from 'src/constants/testIds';
import { connectionSelector } from 'src/selectors/connection';
import { virtualGraphPropertiesSelector } from 'src/selectors/virtualGraphProperties';
import {
  SqlQuotingConvention,
  VirtualGraphOption,
} from 'src/store/state/dataSources';
import {
  ProjectVirtualGraphOptions,
  defaultProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import {
  countUniqueExistingProperties,
  genericPropertyValidator,
  isRowInvalid,
} from 'src/utils/genericPropertyValidation';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

type ProjectVirtualGraphOptionsFormProps = {
  dataSourceType: string | null;
  disableEditing?: boolean;
  setAreResourcePropertiesValid: (areResourcePropertiesValid: boolean) => void;
  setVirtualGraphOptions: (
    virtualGraphOptions: ProjectVirtualGraphOptions
  ) => void;
  updateVirtualGraphOptions: (
    update: Partial<ProjectVirtualGraphOptions>
  ) => void;
  virtualGraphOptions: ProjectVirtualGraphOptions;
};

type FormGroupProps = {
  formProps: ProjectVirtualGraphOptionsFormProps;
  option: VirtualGraphOption;
  testId?: string;
};

const STANDARD_OPTIONS = new Set(Object.values(VirtualGraphOption));

const createInputGroupId = (option: string) =>
  `sd-project-virtual-graph-option__${option}`;

const CheckboxFormGroup: React.VFC<FormGroupProps> = ({
  formProps,
  option,
  testId,
}) => {
  const inputGroupId = createInputGroupId(option);
  return (
    <div className="sd-project-virtual-graph-panel__resource-properties__checkbox-form-group">
      <FormGroup
        className="sd-form-group-reverse"
        inline
        label={
          <Tooltip
            content={
              copy.components.projectResourceDialog.projectVirtualGraphPanel
                .resourceProperties[option].description
            }
            lazy
            position={PopoverPosition.TOP}
          >
            {
              copy.components.projectResourceDialog.projectVirtualGraphPanel
                .resourceProperties[option].label
            }
          </Tooltip>
        }
        labelFor={inputGroupId}
      >
        <Checkbox
          checked={Boolean(formProps.virtualGraphOptions[option])}
          data-testid={testId}
          disabled={formProps.disableEditing}
          id={inputGroupId}
          onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
            formProps.updateVirtualGraphOptions({
              [option]: evt.target.checked,
            });
          }}
        />
      </FormGroup>
    </div>
  );
};

const selectFormGroupItems =
  Object.values(SqlQuotingConvention).map(stringToSelectItem);

const SelectFormGroup: React.VFC<
  FormGroupProps & { itemTestId?: (item: string) => string }
> = ({ itemTestId, formProps, option, testId }) => {
  const selectedItem =
    selectFormGroupItems.find(
      (item) => item.value === formProps.virtualGraphOptions[option]
    ) || null;
  const handleSelectItem = (item: SelectItem | null) => {
    if (item) {
      formProps.updateVirtualGraphOptions({
        [option]: item.value,
      });
    }
  };
  return (
    <div className="sd-project-virtual-graph-panel__resource-properties__select-form-group">
      <FormGroup
        inline
        label={
          <Tooltip
            content={
              copy.components.projectResourceDialog.projectVirtualGraphPanel
                .resourceProperties[option].description
            }
            lazy
            position={PopoverPosition.TOP}
          >
            {
              copy.components.projectResourceDialog.projectVirtualGraphPanel
                .resourceProperties[option].label
            }
          </Tooltip>
        }
      >
        <ItemSelector.Single
          buttonText={selectedItem?.text || ''}
          disabled={formProps.disableEditing}
          filterText={copy.components.defaults.filter}
          items={selectFormGroupItems}
          itemTestId={itemTestId}
          onSelect={handleSelectItem}
          selected={selectedItem}
          testId={testId}
        />
      </FormGroup>
    </div>
  );
};

const TextFormGroup: React.VFC<FormGroupProps> = ({
  formProps,
  option,
  testId,
}) => {
  const isMounted = useIsMounted();
  const inputGroupId = createInputGroupId(option);
  const [localValue, setLocalValue] = React.useState(
    formProps.virtualGraphOptions[option] as string
  );

  const debouncedSetOptions = React.useMemo(
    () =>
      debounce(
        (value: string) => {
          if (isMounted.current) {
            formProps.updateVirtualGraphOptions({ [option]: value });
          }
        },
        500,
        {
          trailing: true,
        }
      ),
    [isMounted, formProps, option]
  );

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = evt.target.value;
    setLocalValue(nextValue);
    debouncedSetOptions(nextValue);
  };

  return (
    <div className="sd-project-virtual-graph-panel__resource-properties__text-form-group">
      <FormGroup
        inline
        label={
          <Tooltip
            content={
              copy.components.projectResourceDialog.projectVirtualGraphPanel
                .resourceProperties[option].description
            }
            lazy
            position={PopoverPosition.TOP}
          >
            {
              copy.components.projectResourceDialog.projectVirtualGraphPanel
                .resourceProperties[option].label
            }
          </Tooltip>
        }
        labelFor={inputGroupId}
      >
        <InputGroup
          data-testid={testId}
          disabled={formProps.disableEditing}
          id={inputGroupId}
          onChange={handleChange}
          placeholder={defaultProjectVirtualGraphOptions[option] as string}
          value={localValue}
        />
      </FormGroup>
    </div>
  );
};

const extractInitialAdvancedProperties = (
  existingOptions: ProjectVirtualGraphOptions
) => {
  return Object.entries(existingOptions)
    .filter(([key]) => !STANDARD_OPTIONS.has(key as VirtualGraphOption))
    .map(([name, value]) => ({
      key: uuid(),
      name,
      value,
    }));
};

const mergeVirtualGraphOptions = (
  existingOptions: ProjectVirtualGraphOptions,
  advancedPropertiesList: GenericProperty[]
) => {
  const existingStandardOptions = Object.values(VirtualGraphOption).reduce(
    (agg, standardOption) => ({
      ...agg,
      [standardOption]: existingOptions[standardOption],
    }),
    {}
  ) as ProjectVirtualGraphOptions;
  return {
    ...existingStandardOptions,
    ...advancedPropertiesList.reduce(
      (agg, keyValuePair) => ({
        ...agg,
        [keyValuePair.name]: keyValuePair.value,
      }),
      {}
    ),
  };
};

export const ProjectVirtualGraphPanelProperties: React.VFC<
  ProjectVirtualGraphOptionsFormProps
> = (formProps) => {
  const {
    dataSourceType,
    disableEditing,
    setAreResourcePropertiesValid,
    setVirtualGraphOptions,
    virtualGraphOptions,
  } = formProps;

  const isMounted = useIsMounted();

  const [advancedProperties, setAdvancedProperties] = React.useState<
    GenericProperty[]
  >(extractInitialAdvancedProperties(virtualGraphOptions));

  const [
    hasAttemptedToCreateInvalidNewRow,
    setHasAttemptedToCreateInvalidNewRow,
  ] = React.useState(false);

  const connection = useReduxSelector(connectionSelector);
  const propertyDescriptions = useReduxSelector(
    virtualGraphPropertiesSelector(connection, dataSourceType),
    isEqual
  );
  const advancedPropertySuggestions = React.useMemo<
    GenericPropertySuggestion[]
  >(
    () =>
      Object.entries(propertyDescriptions)
        .filter(
          ([property]) => !STANDARD_OPTIONS.has(property as VirtualGraphOption)
        )
        .map(([property, propertyDescription]) => {
          const suggestion: GenericPropertySuggestion = {
            name: property,
            description: propertyDescription?.description,
            defaultValue:
              propertyDescription?.default !== undefined
                ? `${propertyDescription?.default}`
                : undefined,
            type:
              (propertyDescription?.type?.toUpperCase() as GenericPropertySuggestionType) ||
              GenericPropertySuggestionType.STRING,
          };
          return suggestion;
        }),
    [propertyDescriptions]
  );

  const debouncedSetOptions = React.useMemo(
    () =>
      debounce(
        (propertyList: GenericProperty[]) => {
          if (isMounted.current) {
            setVirtualGraphOptions(
              mergeVirtualGraphOptions(virtualGraphOptions, propertyList)
            );
          }
        },
        500,
        {
          trailing: true,
        }
      ),
    [isMounted, setVirtualGraphOptions, virtualGraphOptions]
  );

  const setPropertyList = React.useCallback(
    (propertyList: GenericProperty[]) => {
      setAdvancedProperties(propertyList);
      const propertyCounts = countUniqueExistingProperties(propertyList);
      setAreResourcePropertiesValid(
        propertyList.every(
          ({ name, value }) =>
            !isRowInvalid(name, value, STANDARD_OPTIONS, propertyCounts)
        )
      );
      debouncedSetOptions(propertyList);
    },
    [debouncedSetOptions, setAreResourcePropertiesValid]
  );

  return (
    <div className="sd-project-virtual-graph-panel__resource-properties">
      <H6>
        {
          copy.components.projectResourceDialog.projectVirtualGraphPanel.form
            .resourceProperties
        }
      </H6>
      <div className="sd-project-virtual-graph-panel__resource-properties__options">
        <div className="sd-project-virtual-graph-panel__resource-properties__options__row">
          <SelectFormGroup
            formProps={formProps}
            itemTestId={getProjectVirtualGraphPanelSqlQuotingItemTestId}
            option={VirtualGraphOption.SQL_QUOTING_CONVENTION}
            testId={getProjectVirtualGraphPanelPropertyTestId(
              VirtualGraphOption.SQL_QUOTING_CONVENTION
            )}
          />
          <CheckboxFormGroup
            formProps={formProps}
            option={VirtualGraphOption.PERCENT_ENCODE}
            testId={getProjectVirtualGraphPanelPropertyTestId(
              VirtualGraphOption.PERCENT_ENCODE
            )}
          />
        </div>

        <TextFormGroup
          formProps={formProps}
          option={VirtualGraphOption.SQL_FUNCTIONS}
          testId={getProjectVirtualGraphPanelPropertyTestId(
            VirtualGraphOption.SQL_FUNCTIONS
          )}
        />
      </div>
      <AdvancedOptions
        buttonTestId={
          TEST_IDS.projectVirtualGraphPanel.advancedProperties.button
        }
        rightElement={
          <a
            className="sd-project-virtual-graph-panel__resource-properties__advanced-docs-link"
            href={VIRTUAL_GRAPH_OPTIONS_DOCUMENTATION_LINK}
            rel="noreferrer"
            target="_blank"
          >
            <Icon icon={IconNames.INFO_SIGN} />
          </a>
        }
        text={() =>
          copy.components.projectResourceDialog.projectVirtualGraphPanel.resourceProperties.advancedProperties(
            advancedProperties.length
          )
        }
      >
        <ManagedGenericPropertiesEditor
          disabled={disableEditing}
          editorTestId={
            TEST_IDS.projectVirtualGraphPanel.advancedProperties.editor
          }
          maxSuggestions={MAX_PROPERTY_SUGGESTION_ITEMS}
          onPropertiesUpdated={setPropertyList}
          preventAddingNewProperty={({ name, value }) => {
            const invalid = isRowInvalid(
              name,
              value,
              STANDARD_OPTIONS,
              countUniqueExistingProperties(advancedProperties, name)
            );
            setHasAttemptedToCreateInvalidNewRow(invalid);
            return invalid;
          }}
          properties={advancedProperties}
          propertyActionTestId={
            TEST_IDS.projectVirtualGraphPanel.advancedProperties.addButton
          }
          propertyNameTestId={
            TEST_IDS.projectVirtualGraphPanel.advancedProperties.newKey
          }
          propertySuggestions={advancedPropertySuggestions}
          propertyValueTestId={
            TEST_IDS.projectVirtualGraphPanel.advancedProperties.newValue
          }
          rowTestId={getProjectVirtualGraphPanelAdvancedPropertyTestId}
          validator={genericPropertyValidator(
            hasAttemptedToCreateInvalidNewRow,
            STANDARD_OPTIONS,
            advancedProperties
          )}
        />
      </AdvancedOptions>
    </div>
  );
};
